import PublicLayout from "@/components/PublicLayout";
import { Link } from "react-router-dom";
import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Cross, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <PublicLayout hideFooter>
      <section className="celestial-bg min-h-[calc(100vh-4rem)] grid place-items-center px-5 lg:px-8 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="inline-flex w-12 h-12 rounded-full bg-gradient-to-br from-brand-sky to-brand-sage text-white items-center justify-center mb-4 shadow-sm">
              <Cross className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <h1 className="font-serif text-4xl tracking-tight text-brand-ink">
              {sent ? "Check your inbox." : "Forgot your password?"}
            </h1>
            <p className="text-brand-quiet mt-1">
              {sent
                ? "We’ve sent a reset link if that email is registered."
                : "Tell us your email and we’ll send a quiet reset link."}
            </p>
          </div>

          {sent ? (
            <div
              className="bg-white rounded-2xl border border-brand-line p-7 soft-shadow text-center"
              data-testid="forgot-password-sent-card"
            >
              <MailCheck className="w-10 h-10 text-brand-sage mx-auto" strokeWidth={1.5} />
              <p className="mt-5 text-brand-ink">
                If <span className="font-medium">{email}</span> is registered with us, a reset link is on its way.
              </p>
              <p className="mt-3 text-sm text-brand-quiet">
                The link expires in 30 minutes. Check your spam folder if it doesn’t arrive within a minute or two.
              </p>
              <p className="mt-6 scripture-quote text-lg text-brand-quiet">
                “He restores my soul.”
              </p>
              <p className="text-xs text-brand-muted">— Psalm 23:3</p>
              <Link
                to="/login"
                data-testid="forgot-back-to-login"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-5 py-2.5 font-medium"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.8} /> Back to sign in
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-white rounded-2xl border border-brand-line p-7 space-y-4 soft-shadow"
              data-testid="forgot-password-form"
            >
              <div>
                <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Email</label>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
                />
              </div>

              {error && (
                <p
                  data-testid="forgot-error-message"
                  className="text-sm text-[#9a4a3f] bg-[#fbeae6] border border-[#f1cfc6] rounded-lg px-3 py-2"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-button"
                className="w-full rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-6 py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>

              <p className="text-sm text-brand-quiet text-center pt-2">
                Remembered it?{" "}
                <Link to="/login" data-testid="forgot-link-to-login" className="text-brand-skyDark hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
